/**
 * Knowledge documents RPC contract (WP3.7.3b-22d): the firm's knowledge-base
 * library (documents v2, `document_files_v2` and `document_folders_v2`), the
 * knowledge-base hub's Documents section. Replaces the `/api/documents-v2`
 * REST routes (the folder listing, the create-folder, rename and delete
 * actions, `download`, and `upload/prepare`, `upload/retry`,
 * `upload/complete`) with the same behaviour: any member of the firm lists
 * and downloads, only an administrator writes, and only the member who
 * uploaded a file retries its upload.
 *
 * Uploads keep the library's own three-step presigned flow (prepare → PUT to
 * the knowledge-base bucket → complete), not `file_extractions`: the object
 * must land in `S3_KB_DOCS_BUCKET` under `org_{orgId}/` with its Bedrock
 * `metadata.json` sidecar, which the ingestion pipeline reads
 * (file-upload-standard.md, "Documents V2").
 *
 * The firm is never in a payload: every method reads it off the caller
 * (`UserMiddleware`), and `TenancyMiddleware` binds it as `TenancyContext`,
 * so `FileRepository` and `FolderRepository` run their reads and writes in a
 * tenant segment as `cova_app` under the v6.88 and v6.95 walls (proved on
 * docker by p1-files-request-lane; swept by id in p1-rpc-by-id-sweep). Every
 * id is pinned to the caller's firm at its first read. Writes append their
 * ledger events as the caller (`AuditActorMiddleware`).
 *
 * Served at `/api/rpc/knowledge-documents`.
 */
import { Rpc, RpcGroup } from "@effect/rpc"
import { Schema as S } from "effect"

import { AuditActorMiddleware } from "@/contexts/audit/ledger"
import { AllowedApps } from "@/presentation/rpc/middleware/allowed-apps"
import { BillingLockoutMiddleware } from "@/presentation/rpc/middleware/billing-lockout-middleware"
import { RequestIdentityMiddleware } from "@/presentation/rpc/middleware/request-identity-middleware"
import { TenancyMiddleware } from "@/presentation/rpc/middleware/tenancy-middleware"
import { UserMiddleware } from "@/presentation/rpc/middleware/user-middleware"
import { defineWireError } from "@/shared/error-diagnostics/wire"
import { WireErrorSanitizerMiddleware } from "@/shared/error-diagnostics/wire/wire-error-sanitizer-middleware"

/**
 * The knowledge documents rail's wire error:
 *  - DOCUMENTS_ADMIN_REQUIRED    a member who is not an administrator wrote
 *  - DOCUMENTS_NOT_FOUND         the id names no file or folder of the caller's firm
 *  - DOCUMENTS_NOT_OWN_UPLOAD    a retry of a file another member uploaded
 *  - DOCUMENTS_INTERNAL          everything else, via the handler net
 */
export class KnowledgeDocumentsWireError extends defineWireError<KnowledgeDocumentsWireError>()(
  "KnowledgeDocumentsWireError",
  ["DOCUMENTS_ADMIN_REQUIRED", "DOCUMENTS_NOT_FOUND", "DOCUMENTS_NOT_OWN_UPLOAD", "DOCUMENTS_INTERNAL"],
) {}

/** A folder id, or `root` for the top of the library. */
const FolderIdOrRoot = S.optionalWith(S.NonEmptyString, { default: () => "root" })

/** A name as written: trimmed, never empty. */
const Name = S.compose(S.Trim, S.NonEmptyString)

/** Which of the library's two kinds an id names. */
export const KnowledgeDocumentKind = S.Literal("file", "folder")
export type KnowledgeDocumentKind = S.Schema.Type<typeof KnowledgeDocumentKind>

/** A folder as the hub lists it, with how many items it holds. */
export const KnowledgeFolderView = S.Struct({
  id: S.String,
  name: S.String,
  parentId: S.String,
  createdAt: S.NullOr(S.String),
  itemCount: S.Number,
})
export type KnowledgeFolderView = S.Schema.Type<typeof KnowledgeFolderView>

/** A file as the hub lists it: its size and its time already formatted, as the REST route sent them. */
export const KnowledgeFileView = S.Struct({
  id: S.String,
  name: S.String,
  size: S.String,
  type: S.String,
  lastModified: S.String,
  ingestionStatus: S.String,
})
export type KnowledgeFileView = S.Schema.Type<typeof KnowledgeFileView>

/** One folder's contents and its breadcrumb (empty at the root). */
export const KnowledgeDocumentsListing = S.Struct({
  folders: S.Array(KnowledgeFolderView),
  files: S.Array(KnowledgeFileView),
  path: S.Array(S.Struct({ id: S.String, name: S.String })),
})
export type KnowledgeDocumentsListing = S.Schema.Type<typeof KnowledgeDocumentsListing>

/** A presigned PUT for the file's bytes, valid for five minutes. */
export const KnowledgeUploadTicket = S.Struct({ fileId: S.String, uploadUrl: S.String })
export type KnowledgeUploadTicket = S.Schema.Type<typeof KnowledgeUploadTicket>

export const ListKnowledgeDocumentsRpc = Rpc.make("listKnowledgeDocuments", {
  payload: S.Struct({ folderId: FolderIdOrRoot }),
  success: KnowledgeDocumentsListing,
  error: KnowledgeDocumentsWireError,
})

export const CreateKnowledgeFolderRpc = Rpc.make("createKnowledgeFolder", {
  payload: S.Struct({ name: Name, parentId: FolderIdOrRoot }),
  success: KnowledgeFolderView,
  error: KnowledgeDocumentsWireError,
}).middleware(BillingLockoutMiddleware)

export const RenameKnowledgeDocumentRpc = Rpc.make("renameKnowledgeDocument", {
  payload: S.Struct({ id: S.String, kind: KnowledgeDocumentKind, name: Name }),
  success: S.Void,
  error: KnowledgeDocumentsWireError,
}).middleware(BillingLockoutMiddleware)

/** A file is soft-deleted (the pipeline removes it from the index); a folder goes with everything under it. */
export const DeleteKnowledgeDocumentRpc = Rpc.make("deleteKnowledgeDocument", {
  payload: S.Struct({ id: S.String, kind: KnowledgeDocumentKind }),
  success: S.Void,
  error: KnowledgeDocumentsWireError,
}).middleware(BillingLockoutMiddleware)

/** A short-lived signed URL that downloads the file as an attachment under its name. */
export const GetKnowledgeDocumentDownloadUrlRpc = Rpc.make("getKnowledgeDocumentDownloadUrl", {
  payload: S.Struct({ fileId: S.String }),
  success: S.Struct({ url: S.String }),
  error: KnowledgeDocumentsWireError,
})

export const PrepareKnowledgeUploadRpc = Rpc.make("prepareKnowledgeUpload", {
  payload: S.Struct({
    fileName: S.NonEmptyString,
    fileSize: S.Int.pipe(S.positive()),
    fileType: S.optionalWith(S.NonEmptyString, { default: () => "application/octet-stream" }),
    folderId: FolderIdOrRoot,
  }),
  success: KnowledgeUploadTicket,
  error: KnowledgeDocumentsWireError,
}).middleware(BillingLockoutMiddleware)

/** A fresh PUT for a file whose upload failed; DOCUMENTS_NOT_FOUND tells the client to prepare anew. */
export const RetryKnowledgeUploadRpc = Rpc.make("retryKnowledgeUpload", {
  payload: S.Struct({ fileId: S.String }),
  success: KnowledgeUploadTicket,
  error: KnowledgeDocumentsWireError,
}).middleware(BillingLockoutMiddleware)

/** The bytes are in: write the file's ingestion sidecar. */
export const CompleteKnowledgeUploadRpc = Rpc.make("completeKnowledgeUpload", {
  payload: S.Struct({ fileId: S.String }),
  success: S.Void,
  error: KnowledgeDocumentsWireError,
}).middleware(BillingLockoutMiddleware)

export const KnowledgeDocumentsRpc = RpcGroup.make(
  ListKnowledgeDocumentsRpc,
  CreateKnowledgeFolderRpc,
  RenameKnowledgeDocumentRpc,
  DeleteKnowledgeDocumentRpc,
  GetKnowledgeDocumentDownloadUrlRpc,
  PrepareKnowledgeUploadRpc,
  RetryKnowledgeUploadRpc,
  CompleteKnowledgeUploadRpc,
)
  .annotateRpcs(AllowedApps, ["platform"])
  .middleware(UserMiddleware)
  .middleware(AuditActorMiddleware)
  .middleware(RequestIdentityMiddleware)
  // The caller's firm as `TenancyContext` (004 §6 order): the repositories'
  // reads and writes run in a tenant segment as `cova_app` under the walls.
  .middleware(TenancyMiddleware)
  // The structural safety net, attached LAST so it covers every rpc above.
  .middleware(WireErrorSanitizerMiddleware)
